import React, { useState } from 'react';
import axios from 'axios';
import {
  Box,
  Card,
  Typography,
  Button,
  Alert,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { CloudUpload as CloudUploadIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import API_BASE_URL from '../config/api';

const UploadCSV = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.type === 'text/csv' || selectedFile.name.endsWith('.csv')) {
        setFile(selectedFile);
        setError('');
        setResult(null);
      } else {
        setError('Please select a CSV file');
        setFile(null);
      }
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    setUploading(true);
    setError('');
    setResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/upload/csv`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setResult(response.data);
      setFile(null);
      document.getElementById('file-input').value = '';
    } catch (err) {
      setError(
        err.response?.data?.message || 'Error uploading file. Please try again.'
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: '#1e293b' }}>
          Upload Employee Data
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Upload CSV file to import employee and payroll data
        </Typography>
      </Box>

      <Card sx={{ p: 4, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <Box sx={{ mb: 3 }}>
          <input
            accept=".csv"
            style={{ display: 'none' }}
            id="file-input"
            type="file"
            onChange={handleFileChange}
            disabled={uploading}
          />
          <label htmlFor="file-input">
            <Button
              variant="outlined"
              component="span"
              startIcon={<CloudUploadIcon />}
              disabled={uploading}
              fullWidth
              sx={{
                  py: 3,
                  borderStyle: 'dashed',
                  borderWidth: 2,
                  borderRadius: 1,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 500,
                }}
            >
              {file ? file.name : 'Select CSV File'}
            </Button>
          </label>
          {file && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
              Selected: {file.name} ({(file.size / 1024).toFixed(2)} KB)
            </Typography>
          )}
        </Box>

        {uploading && (
          <Box sx={{ mb: 3 }}>
            <LinearProgress sx={{ borderRadius: 1, height: 8 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Uploading and processing...
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {result && (
          <Alert 
            severity="success" 
            icon={<CheckCircleIcon />}
            sx={{ mb: 3, borderRadius: 2 }}
          >
            {result.message}
          </Alert>
        )}

        {result && result.stats && (
          <TableContainer component={Paper} sx={{ mt: 2, mb: 3, borderRadius: 1 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Metric</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Total Rows</TableCell>
                  <TableCell align="right">{result.stats.totalRows}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Employees Processed</TableCell>
                  <TableCell align="right">
                    {result.stats.employeesProcessed}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Payrolls Processed</TableCell>
                  <TableCell align="right">
                    {result.stats.payrollsProcessed}
                  </TableCell>
                </TableRow>
                {result.stats.errors > 0 && (
                  <TableRow>
                    <TableCell>Errors</TableCell>
                    <TableCell align="right">{result.stats.errors}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {result && result.errors && result.errors.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Errors encountered:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {result.errors.slice(0, 10).map((err, idx) => (
                <li key={idx}>{err}</li>
              ))}
              {result.errors.length > 10 && (
                <li>... and {result.errors.length - 10} more errors</li>
              )}
            </ul>
          </Alert>
        )}

        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={!file || uploading}
          fullWidth
          sx={{
            mt: 2,
            py: 1.5,
            borderRadius: 1,
            textTransform: 'none',
            fontSize: '1rem',
            fontWeight: 600,
            boxShadow: '0 4px 14px 0 rgba(99, 102, 241, 0.39)',
          }}
        >
          {uploading ? 'Uploading...' : 'Upload and Process'}
        </Button>
      </Card>
    </Box>
  );
};

export default UploadCSV;
